import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Database,
  Table,
  Unlink,
  Link2,
  AlertCircle,
  Settings,
  Search,
  Terminal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { datasetApi } from '../api/datasetApi';
import { bigqueryApi } from '../api/bigqueryApi';
import { udfApi } from '../api/udfApi';
import ConnectModal from '../components/workspace/ConnectModal';
import ConfirmModal from '../components/common/ConfirmModal';
import PreviewTable from '../components/workspace/PreviewTable';
import BillingProjectModal from '../components/workspace/BillingProjectModal';
import FunctionDetailModal from '../components/functions/FunctionDetailModal';
import FunctionImportModal from '../components/functions/FunctionImportModal';
import TreeTable from '../components/common/TreeTable';
import { cn } from '../lib/utils';
import { showErrorToast, showInfoToast, showSuccessToast } from '../lib/toast';

const ConnectionRow = ({ type, status, billingProject, onConnect, onDisconnect, onSettings, icon: Icon, color }) => {
  const isActive = status === 'connected' || status === 'active';
  const isPendingConfig = type === 'bigquery' && isActive && !billingProject;

  return (
    <div className="flex items-center p-4 bg-white dark:bg-gray-900 border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
      <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800">
        <Icon className={cn("w-6 h-6", color)} />
      </div>

      <div className="flex-1 min-w-0 ml-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">
            {type === 'manteo' ? 'Manteo Warehouse' : 'BigQuery'}
          </h4>
          {isPendingConfig && (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg animate-pulse">
              <AlertCircle size={10} />
              <span className="text-[8px] font-black uppercase tracking-widest">Setup Required</span>
            </div>
          )}
        </div>
        <p className="text-[10px] text-gray-500 font-medium truncate mt-0.5">
          {type === 'manteo'
            ? 'Internal High-Performance Storage'
            : isActive
              ? (billingProject ? `Billing Project: ${billingProject}` : 'Project Selection Required')
              : 'Authenticate via Google SSO'}
        </p>
      </div>

      <div className="flex items-center gap-2 ml-4">
        {type === 'bigquery' && isActive && (
          <>
            <button
              onClick={onSettings}
              className={cn(
                "p-2 rounded-lg transition-all",
                isPendingConfig
                  ? "text-amber-600 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100"
                  : "text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              )}
              title="Settings"
            >
              <Settings size={16} className={cn(isPendingConfig && "animate-bounce")} />
            </button>
            <button
              onClick={onDisconnect}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
              title="Disconnect"
            >
              <Unlink size={16} />
            </button>
          </>
        )}
        {!isActive && (
          <button
            onClick={onConnect}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-sm"
          >
            <Link2 size={12} strokeWidth={3} />
            Connect
          </button>
        )}
      </div>
    </div>
  );
};

const DataSourcesPage = () => {
  useEffect(() => {
    document.title = 'manteo.ai | Data Sources';
  }, []);
  const [bqStatus, setBqStatus] = useState({ connected: false });
  const [datasets, setDatasets] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isFunctionImportModalOpen, setIsFunctionImportModalOpen] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [resumeAfterBilling, setResumeAfterBilling] = useState(null); // null | 'dataset' | 'function'
  const [modalMode, setModalMode] = useState('table'); // 'table' | 'connection'

  // Preview modal state
  const [previewDataset, setPreviewDataset] = useState(null);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [datasetSearchQuery, setDatasetSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('datasets');

  // Sorting state
  const [sortColumn, setSortColumn] = useState('created_at'); // 'name' | 'created_at'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' | 'desc'

  // Tree expansion state
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedDatasets, setExpandedDatasets] = useState(new Set());
  const [expandedLocalGroups, setExpandedLocalGroups] = useState(new Set());

  // Function tree expansion state
  const [expandedFunctionProjects, setExpandedFunctionProjects] = useState(new Set());
  const [expandedFunctionDatasets, setExpandedFunctionDatasets] = useState(new Set());

  // Function sorting state
  const [functionSortColumn, setFunctionSortColumn] = useState('created_at'); // 'name' | 'created_at'
  const [functionSortDirection, setFunctionSortDirection] = useState('desc'); // 'asc' | 'desc'

  // Confirmation modal states
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => { },
    variant: 'danger'
  });


  useEffect(() => {
    loadData();

    // Handle Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bigquery-connected')) {
      // Clear the param from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // We'll use a local check once data is loaded to trigger the modal
      const finalizeConnection = async () => {
        const bq = await bigqueryApi.getStatus();
        if (bq.connected && !bq.billing_project_id) {
          handleOpenSettings();
        }
      };
      finalizeConnection();
    }
  }, []);

  useEffect(() => {
    const hasPending = datasets.some(d => d.status === 'loading' || d.status === 'generating');
    if (hasPending && !isPolling) {
      setIsPolling(true);
    } else if (!hasPending && isPolling) {
      setIsPolling(false);
    }
  }, [datasets, isPolling]);

  useEffect(() => {
    let interval;
    if (isPolling) {
      interval = setInterval(() => {
        loadData();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isPolling]);

  const loadData = async () => {
    try {
      const [bq, ds, udfs] = await Promise.all([
        bigqueryApi.getStatus(),
        datasetApi.getDatasets(),
        udfApi.listUDFs()
      ]);
      setBqStatus(bq || { connected: false });
      setDatasets(ds || []);
      setFunctions(udfs || []);
    } catch (err) {
      console.error('Failed to load data sources:', err);
    }
  };

  const handleConnect = async (type, data) => {
    if (type === 'bigquery' && data) {
      try {
        await datasetApi.importBigQueryTables(data);
        showInfoToast('Import started. New datasets will appear shortly.');
      } catch (err) {
        console.error('Failed to import BigQuery tables:', err);
        showErrorToast('Could not import selected tables. Please try again.');
      }
    }
    // Refresh connection status and inventory
    await loadData();

    if (type === 'upload' || type === 'sample' || type === 'library') {
      showSuccessToast('Dataset added.');
    }

    // Notify OnboardingBanner to refresh
    window.dispatchEvent(new CustomEvent('datasetAdded'));

    // Auto-unfold relevant sections
    if (type === 'bigquery') {
      // We can't easily trigger unfold in DatasetTreeView without lifting state or using a ref
      // For now, let's just make sure DataSourcesPage refresh is visible.
    }
  };

  const handleDisconnectBQ = async () => {
    setConfirmConfig({
      isOpen: true,
      title: 'Disconnect BigQuery?',
      description: 'Are you sure you want to disconnect BigQuery? ALL imported BigQuery tables will be removed from your inventory.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await bigqueryApi.disconnect();
          await loadData();
          showSuccessToast('BigQuery disconnected.');
        } catch (err) {
          console.error('Failed to disconnect BigQuery:', err);
        }
      }
    });
  };

  const handleRemoveDataset = async (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Remove Dataset?',
      description: 'This will remove the dataset from your platform library. The original in BigQuery will not be affected.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await datasetApi.deleteDataset(id);
          await loadData();
          showSuccessToast('Dataset removed.');
          // Notify OnboardingBanner to refresh
          window.dispatchEvent(new CustomEvent('datasetRemoved'));
        } catch (err) {
          console.error('Failed to remove dataset:', err);
        }
      }
    });
  };

  const handleRemoveFunction = async (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Remove Function?',
      description: 'This will remove the function from your platform library. The original in BigQuery will not be affected.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await udfApi.deleteUDF(id);
          await loadData();
          showSuccessToast('Function removed.');
          if (selectedFunction?.id === id) {
            setSelectedFunction(null);
          }
        } catch (err) {
          console.error('Failed to remove function:', err);
        }
      }
    });
  };

  const handleRebuildDataset = async (id) => {
    try {
      await datasetApi.rebuildMetadata(id);
      await loadData();
      setIsPolling(true); // Start polling for status updates
      showInfoToast('Metadata refresh started.');
    } catch (err) {
      console.error('Failed to rebuild dataset metadata:', err);
    }
  };

  const openAddTable = () => {
    setModalMode('table');
    setIsConnectModalOpen(true);
  };

  const openAddConnection = () => {
    setModalMode('connection');
    setIsConnectModalOpen(true);
  };

  const handleOpenSettings = () => {
    setIsBillingModalOpen(true);
  };

  // Filter and sort functions
  const filteredFunctions = useMemo(() => {
    let filtered = functions.filter(func => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = func.name.toLowerCase().includes(q) ||
        (func.description && func.description.toLowerCase().includes(q)) ||
        (func.signature && func.signature.toLowerCase().includes(q));
      return matchesSearch;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (functionSortColumn) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return functionSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return functionSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [functions, searchQuery, functionSortColumn, functionSortDirection]);

  // Build BigQuery functions tree structure (project -> dataset -> functions)
  const bigQueryFunctionsTree = useMemo(() => {
    const tree = {};
    const bqFunctions = filteredFunctions.filter(f => f.source === 'bigquery' && f.routine_id);

    bqFunctions.forEach(func => {
      // Parse routine_id (project.dataset.function_name)
      const parts = func.routine_id.split('.');
      if (parts.length >= 3) {
        const project = parts[0];
        const dataset = parts[1];
        const functionName = parts.slice(2).join('.');

        if (!tree[project]) {
          tree[project] = {};
        }
        if (!tree[project][dataset]) {
          tree[project][dataset] = [];
        }
        tree[project][dataset].push({
          ...func,
          project,
          dataset,
          functionName
        });
      }
    });

    return tree;
  }, [filteredFunctions]);

  // Separate custom functions
  const customFunctions = useMemo(() => {
    return filteredFunctions.filter(f => f.source === 'custom');
  }, [filteredFunctions]);

  // Flatten datasets for table view and filter
  const filteredDatasets = useMemo(() => {
    const allTables = [];
    const sourcesList = Array.isArray(datasets) ? datasets : [];

    sourcesList.forEach(d => {
      if (d.type === 'upload' || d.type === 'parquet' || d.type === 'csv') {
        allTables.push({
          id: d.id,
          name: d.name,
          type: d.type,
          status: d.status,
          source: 'manteo',
          displayName: d.name,
          created_at: d.created_at
        });
      } else if (d.type === 'bigquery') {
        // Parse BigQuery table ID (project.dataset.table)
        const parts = d.id.split('.');
        if (parts.length === 3) {
          const [project, dataset, table] = parts;
          allTables.push({
            id: d.id,
            name: table,
            type: d.type,
            status: d.status,
            source: 'bigquery',
            project: project,
            dataset: dataset,
            displayName: `${project}.${dataset}.${table}`,
            created_at: d.created_at
          });
        } else {
          // Fallback for non-standard IDs
          allTables.push({
            id: d.id,
            name: d.name || d.id,
            type: d.type,
            status: d.status,
            source: 'bigquery',
            displayName: d.name || d.id,
            created_at: d.created_at
          });
        }
      }
    });

    // Filter by search query
    const q = datasetSearchQuery.toLowerCase();
    let filtered = allTables.filter(table =>
      table.displayName.toLowerCase().includes(q) ||
      table.name.toLowerCase().includes(q) ||
      (table.project && table.project.toLowerCase().includes(q)) ||
      (table.dataset && table.dataset.toLowerCase().includes(q))
    );

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [datasets, datasetSearchQuery, sortColumn, sortDirection]);

  // Build BigQuery tree structure (project -> dataset -> tables)
  const bigQueryTree = useMemo(() => {
    const tree = {};
    const bqTables = filteredDatasets.filter(t => t.source === 'bigquery' && t.project && t.dataset);

    bqTables.forEach(table => {
      if (!tree[table.project]) {
        tree[table.project] = {};
      }
      if (!tree[table.project][table.dataset]) {
        tree[table.project][table.dataset] = [];
      }
      tree[table.project][table.dataset].push(table);
    });

    return tree;
  }, [filteredDatasets]);

  // Build Manteo/Local datasets tree structure (group by type)
  const localDatasetsTree = useMemo(() => {
    const tree = {};
    const localTables = filteredDatasets.filter(t => t.source === 'manteo');

    localTables.forEach(table => {
      const type = table.type || 'other';
      if (!tree[type]) {
        tree[type] = [];
      }
      tree[type].push(table);
    });

    return tree;
  }, [filteredDatasets]);

  // Check if we have datasets for each source
  const hasBigQueryDatasets = useMemo(() => {
    return Object.keys(bigQueryTree).length > 0;
  }, [bigQueryTree]);

  const hasLocalDatasets = useMemo(() => {
    return Object.keys(localDatasetsTree).length > 0;
  }, [localDatasetsTree]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleLocalGroup = (groupKey) => {
    setExpandedLocalGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const toggleProject = (project) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(project)) {
        next.delete(project);
      } else {
        next.add(project);
      }
      return next;
    });
  };

  const toggleDataset = (project, dataset) => {
    const key = `${project}.${dataset}`;
    setExpandedDatasets(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleFunctionProject = (project) => {
    setExpandedFunctionProjects(prev => {
      const next = new Set(prev);
      if (next.has(project)) {
        next.delete(project);
      } else {
        next.add(project);
      }
      return next;
    });
  };

  const toggleFunctionDataset = (project, dataset) => {
    const key = `${project}.${dataset}`;
    setExpandedFunctionDatasets(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleFunctionSort = (column) => {
    if (functionSortColumn === column) {
      setFunctionSortDirection(functionSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setFunctionSortColumn(column);
      setFunctionSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const hasActiveConnections = !!bqStatus.connected;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 dark:bg-gray-950 px-8 py-12 transition-colors duration-300">
      <ConnectModal
        isOpen={isConnectModalOpen}
        mode={modalMode}
        onClose={() => setIsConnectModalOpen(false)}
        onConnect={handleConnect}
        initialStep={resumeAfterBilling === 'dataset' ? 'bigquery-dataset' : 'select'}
        onBillingRequired={() => {
          setIsConnectModalOpen(false);
          setResumeAfterBilling('dataset');
          setIsBillingModalOpen(true);
        }}
        currentSources={datasets}
      />

      <FunctionImportModal
        isOpen={isFunctionImportModalOpen}
        onClose={() => setIsFunctionImportModalOpen(false)}
        importedFunctions={functions}
        onImportComplete={async () => {
          await loadData();
          setIsFunctionImportModalOpen(false);
          showSuccessToast('Functions imported.');
        }}
        onBillingRequired={() => {
          setIsFunctionImportModalOpen(false);
          setResumeAfterBilling('function');
          setIsBillingModalOpen(true);
        }}
      />

      <BillingProjectModal
        isOpen={isBillingModalOpen}
        onClose={() => {
          setIsBillingModalOpen(false);
          setResumeAfterBilling(null);
        }}
        onProjectSelected={async () => {
          await loadData();
          showSuccessToast('Billing project updated.');
          if (resumeAfterBilling === 'dataset') {
            setResumeAfterBilling(null);
            setIsConnectModalOpen(true);
          } else if (resumeAfterBilling === 'function') {
            setResumeAfterBilling(null);
            setIsFunctionImportModalOpen(true);
          }
        }}
        currentBillingProject={bqStatus.billing_project_id}
      />

      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        description={confirmConfig.description}
        variant={confirmConfig.variant}
        onConfirm={confirmConfig.onConfirm}
        onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
      />

      {previewDataset && (
        <PreviewTable
          dataset={previewDataset}
          onClose={() => setPreviewDataset(null)}
        />
      )}

      <FunctionDetailModal
        func={functions.find(f => f.id === selectedFunction?.id)}
        isOpen={!!selectedFunction}
        onClose={() => setSelectedFunction(null)}
        onUpdate={loadData}
      />

      <div className="max-w-7xl mx-auto space-y-12">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Data Sources</h1>
          </div>
        </header>

        <section className="space-y-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <Link2 size={16} className="text-gray-400 dark:text-gray-500" />
              <h2 className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Active Connections</h2>
            </div>
            <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-6 leading-relaxed max-w-2xl">
              Connect to external data sources to import datasets and functions.
            </p>
            <div className="space-y-3 max-w-2xl">
              {hasBigQueryDatasets && (
                <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[24px] overflow-hidden shadow-sm">
                  <ConnectionRow
                    type="bigquery"
                    status={hasActiveConnections ? "connected" : "disconnected"}
                    billingProject={bqStatus.billing_project_id}
                    onDisconnect={handleDisconnectBQ}
                    onSettings={handleOpenSettings}
                    onConnect={openAddConnection}
                    icon={Database}
                    color="text-blue-600"
                  />
                </div>
              )}
              {hasLocalDatasets && (
                <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[24px] overflow-hidden shadow-sm">
                  <div className="flex items-center p-4 bg-white dark:bg-gray-900 border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800">
                      <Database className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">
                          Local Datasets
                        </h4>
                      </div>
                      <p className="text-[10px] text-gray-500 font-medium truncate mt-0.5">
                        Manteo Warehouse - Internal High-Performance Storage
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {!hasBigQueryDatasets && !hasLocalDatasets && (
                <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[24px] shadow-sm p-10 text-center max-w-2xl">
                  <p className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                    No active connections
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                    Add a connection to start importing datasets and functions.
                  </p>
                  <button
                    onClick={openAddConnection}
                    className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-sm active:scale-95"
                  >
                    <Plus size={14} strokeWidth={3} />
                    Add Connection
                  </button>
                </div>
              )}
            </div>
          </div>

        </section>

        <section className="space-y-6 pt-10 border-t border-gray-50 dark:border-gray-800/50">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-gray-400 dark:text-gray-500" />
            <h2 className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Platform Library</h2>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 w-fit">
              <button
                onClick={() => setActiveTab('datasets')}
                className={cn(
                  "flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                  activeTab === 'datasets'
                    ? "bg-white dark:bg-gray-800 text-emerald-600 shadow-sm"
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                )}
              >
                <Database size={14} />
                Datasets
              </button>
              <button
                onClick={() => setActiveTab('functions')}
                className={cn(
                  "flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                  activeTab === 'functions'
                    ? "bg-white dark:bg-gray-800 text-emerald-600 shadow-sm"
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                )}
              >
                <Terminal size={14} />
                Functions
              </button>
            </div>

          </div>
        </section>

        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {activeTab === 'datasets' ? (
            <section className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex gap-2">
                  <button
                    onClick={openAddTable}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-md active:scale-95"
                  >
                    <Plus size={14} strokeWidth={3} />
                    Add Dataset
                  </button>
                  {!hasActiveConnections && (
                    <button
                      onClick={openAddConnection}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-md active:scale-95"
                    >
                      <Plus size={14} strokeWidth={3} />
                      Connect Source
                    </button>
                  )}
                  {(Object.keys(bigQueryTree).length > 0 || Object.keys(localDatasetsTree).length > 0) && (
                    <div className="flex items-center gap-1 border-l border-gray-200 dark:border-gray-700 pl-2 ml-2">
                      <button
                        onClick={() => {
                          // Expand all
                          const allProjects = Object.keys(bigQueryTree);
                          const allLocalGroups = Object.keys(localDatasetsTree);
                          const allDatasets = new Set();
                          allProjects.forEach(project => {
                            setExpandedProjects(prev => new Set([...prev, project]));
                            Object.keys(bigQueryTree[project]).forEach(dataset => {
                              allDatasets.add(`${project}.${dataset}`);
                            });
                          });
                          setExpandedDatasets(allDatasets);
                          setExpandedLocalGroups(new Set(allLocalGroups));
                        }}
                        className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        title="Expand All"
                      >
                        Expand All
                      </button>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <button
                        onClick={() => {
                          // Collapse all
                          setExpandedProjects(new Set());
                          setExpandedDatasets(new Set());
                          setExpandedLocalGroups(new Set());
                        }}
                        className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        title="Collapse All"
                      >
                        Collapse All
                      </button>
                    </div>
                  )}
                </div>
                <div className="relative group min-w-[240px]">
                  <Search size={14} strokeWidth={2.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="SEARCH DATASETS..."
                    value={datasetSearchQuery}
                    onChange={(e) => setDatasetSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl text-[10px] font-black uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 w-full transition-all shadow-sm"
                  />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[28px] overflow-hidden shadow-xl min-h-[500px]">
                {filteredDatasets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-32 text-center">
                    <div className="p-8 bg-gray-50 dark:bg-gray-800/50 rounded-[40px] shadow-sm mb-6 border border-gray-100 dark:border-gray-800">
                      <Database size={56} strokeWidth={1} className="text-gray-200 dark:text-gray-700" />
                    </div>
                    <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                      {datasetSearchQuery ? 'No matches found' : 'No datasets registered'}
                    </h3>
                    <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-2 max-w-[300px] leading-relaxed">
                      {datasetSearchQuery
                        ? 'Try adjusting your search or filters'
                        : 'Import datasets to index them and make them smart and understandable to AI agents. Datasets are not copied.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/50 dark:bg-gray-950/50 border-b border-gray-100 dark:border-gray-800">
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleSort('name')}
                          >
                            <div className="flex items-center gap-2">
                              Dataset Name
                              {sortColumn === 'name' ? (
                                sortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleSort('created_at')}
                          >
                            <div className="flex items-center gap-2">
                              Added At
                              {sortColumn === 'created_at' ? (
                                sortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-right text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-800/30">
                        <TreeTable
                          bigQueryTree={bigQueryTree}
                          localGroups={localDatasetsTree}
                          expandedProjects={expandedProjects}
                          expandedDatasets={expandedDatasets}
                          expandedLocalGroups={expandedLocalGroups}
                          onToggleProject={toggleProject}
                          onToggleDataset={toggleDataset}
                          onToggleLocalGroup={toggleLocalGroup}
                          onSelectItem={(item) => {
                            if (item.status === 'ready') {
                              setPreviewDataset({ id: item.id, name: item.name });
                            }
                          }}
                          onRemoveItem={handleRemoveDataset}
                          onRetryItem={handleRebuildDataset}
                          formatDate={formatDate}
                          itemIcon={Table}
                          projectIconColor="text-blue-500"
                          datasetIconColor="text-blue-400"
                          localGroupIconColor="text-emerald-500"
                          showStatus={true}
                          getItemStatus={(item) => item.status}
                          getDisplayName={(item) => item.displayName}
                        />
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          ) : (
            <section className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsFunctionImportModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-[9px] uppercase tracking-widest shadow-md active:scale-95"
                  >
                    <Plus size={14} strokeWidth={3} />
                    Add Function
                  </button>
                  {(Object.keys(bigQueryFunctionsTree).length > 0 || customFunctions.length > 0) && (
                    <div className="flex items-center gap-1 border-l border-gray-200 dark:border-gray-700 pl-2 ml-2">
                      <button
                        onClick={() => {
                          // Expand all
                          const allProjects = Object.keys(bigQueryFunctionsTree);
                          const allDatasets = new Set();
                          allProjects.forEach(project => {
                            setExpandedFunctionProjects(prev => new Set([...prev, project]));
                            Object.keys(bigQueryFunctionsTree[project]).forEach(dataset => {
                              allDatasets.add(`${project}.${dataset}`);
                            });
                          });
                          setExpandedFunctionDatasets(allDatasets);
                        }}
                        className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        title="Expand All"
                      >
                        Expand All
                      </button>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <button
                        onClick={() => {
                          // Collapse all
                          setExpandedFunctionProjects(new Set());
                          setExpandedFunctionDatasets(new Set());
                        }}
                        className="px-2 py-1 text-[8px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        title="Collapse All"
                      >
                        Collapse All
                      </button>
                    </div>
                  )}
                </div>
                <div className="relative group min-w-[240px]">
                  <Search size={14} strokeWidth={2.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="SEARCH FUNCTIONS..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl text-[10px] font-black uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 w-full transition-all shadow-sm"
                  />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[28px] overflow-hidden shadow-xl min-h-[500px]">
                {filteredFunctions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-32 text-center">
                    <div className="p-8 bg-gray-50 dark:bg-gray-800/50 rounded-[40px] shadow-sm mb-6 border border-gray-100 dark:border-gray-800">
                      <Terminal size={56} strokeWidth={1} className="text-gray-200 dark:text-gray-700" />
                    </div>
                    <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">
                      {searchQuery ? 'No matches found' : 'No functions registered'}
                    </h3>
                    <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-2 max-w-[300px] leading-relaxed">
                      {searchQuery
                        ? 'Try adjusting your search or filters'
                        : 'Import functions to index them and make them smart and understandable to AI agents.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/50 dark:bg-gray-950/50 border-b border-gray-100 dark:border-gray-800">
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleFunctionSort('name')}
                          >
                            <div className="flex items-center gap-2">
                              Function Name
                              {functionSortColumn === 'name' ? (
                                functionSortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                            onClick={() => handleFunctionSort('created_at')}
                          >
                            <div className="flex items-center gap-2">
                              Added At
                              {functionSortColumn === 'created_at' ? (
                                functionSortDirection === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                              ) : (
                                <ArrowUpDown size={11} className="opacity-30" />
                              )}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-right text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-800/30">
                        <TreeTable
                          bigQueryTree={bigQueryFunctionsTree}
                          customGroup={customFunctions}
                          expandedProjects={expandedFunctionProjects}
                          expandedDatasets={expandedFunctionDatasets}
                          onToggleProject={toggleFunctionProject}
                          onToggleDataset={toggleFunctionDataset}
                          onSelectItem={setSelectedFunction}
                          onRemoveItem={handleRemoveFunction}
                          formatDate={formatDate}
                          itemIcon={Terminal}
                          projectIconColor="text-blue-500"
                          datasetIconColor="text-blue-400"
                          localGroupIconColor="text-emerald-500"
                          showStatus={false}
                          getItemName={(item) => item.functionName || item.name}
                          getDisplayName={(item) => item.signature || item.name}
                        />
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSourcesPage;
